/**
 * A chamada — a tela principal da demonstração.
 *
 * É aqui que a conversa acontece: esta tela abre o microfone, segura a conexão
 * WebRTC e inicia a chamada — na Realtime API (Modelo A, `/linha`) ou no
 * pipeline ASR + GPT + TTS (Modelo B, `/linha-2`). Funciona **sozinha**; o
 * console de supervisão (`/console`) é opcional e apenas espelha o que
 * acontece aqui.
 *
 * Por isso a tela não mostra teto, política nem raciocínio: quem opera aqui está
 * fazendo o papel da devedora, e a devedora não vê nada disso.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'

import { api } from '../lib/api'
import { brl, clock } from '../lib/format'
import { connectPipeline } from '../lib/pipeline'
import { connectRealtime, createAmplitudeAnalyser, type RealtimeHandle } from '../lib/realtime'
import { openStream, type StreamHandle, type StreamMessage } from '../lib/stream'
import { color, radius, shadow } from '../theme/tokens'
import { Orb } from '../components/demo/Orb'
import type { OrbState } from '../lib/callEngine'
import type {
  Artifact,
  Case,
  RealtimeStatus,
  Scenario,
  Speaker,
  ToolExecutionResponse,
} from '../lib/types'

type Phase = 'setup' | 'connecting' | 'live' | 'ended'

/**
 * A: fala↔fala na Realtime API. B: pipeline ASR + GPT + TTS — mesma chave,
 * mesma política, mesmo contrato de tela; muda só quem conecta.
 */
export type Variant = 'a' | 'b'

// Sem jargão nas telas que o cliente vê. Com o Modelo A fora do menu, o "B"
// também saiu do rótulo — falar de um modelo só levanta a pergunta pelo outro.
export const VARIANT_LABEL: Record<Variant, string> = {
  a: 'Agente de voz · Modelo A',
  b: 'Agente de voz',
}

interface Caption {
  /** Identidade estável do balão: a animação de entrada roda uma vez só. */
  id: number
  speaker: Speaker
  text: string
}

export function Linha({ variant = 'a' }: { variant?: Variant }) {
  const [phase, setPhase] = useState<Phase>('setup')
  const [cases, setCases] = useState<Case[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [realtime, setRealtime] = useState<RealtimeStatus | null>(null)
  const [scenarioId, setScenarioId] = useState('2')
  const [agentName, setAgentName] = useState('Alice')

  const [elapsed, setElapsed] = useState(0)
  const [orb, setOrb] = useState<OrbState>('idle')
  const [muted, setMuted] = useState(false)
  const [agentPaused, setAgentPaused] = useState(false)
  const [captions, setCaptions] = useState<Caption[]>([])
  const [showCaptions, setShowCaptions] = useState(true)
  const [whisperFlash, setWhisperFlash] = useState<string | null>(null)
  const [supervisors, setSupervisors] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // A ferramenta continuar_whatsapp executou: o "cobrado" ganha o botão de
  // migrar de canal levando a conversa junto.
  const [handoffReady, setHandoffReady] = useState(false)
  const navigate = useNavigate()

  const stream = useRef<StreamHandle | null>(null)
  const captionSeq = useRef(0)
  const realtimeHandle = useRef<RealtimeHandle | null>(null)
  const amplitude = useRef<(() => number) | null>(null)
  const analyserDispose = useRef<(() => void) | null>(null)
  const callId = useRef<string | null>(null)
  const elapsedRef = useRef(0)
  const phaseRef = useRef<Phase>('setup')

  elapsedRef.current = elapsed
  phaseRef.current = phase

  const at = useCallback(() => clock(elapsedRef.current), [])

  const publish = useCallback(
    (message: Parameters<StreamHandle['send']>[0]) => stream.current?.send(message),
    [],
  )

  const scenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  )
  const debtor = useMemo(
    () => cases.find((c) => c.id === scenario?.case_id) ?? null,
    [cases, scenario],
  )

  useEffect(() => {
    // O status do Modelo B tem os mesmos campos que a tela usa; o mapeamento
    // evita um segundo caminho de renderização.
    const status: Promise<RealtimeStatus> =
      variant === 'b'
        ? api.pipelineStatus().then((s) => ({
            enabled: s.enabled,
            model: s.chat_model,
            voice: s.voice,
            calls_url: s.calls_url,
            policy_version: s.policy_version,
            tools: s.tools,
            reason: s.reason,
          }))
        : api.realtimeStatus()

    Promise.all([api.listCases(), api.listScenarios(), status, api.getPublished()])
      .then(([caseList, scenarioList, statusValue, policy]) => {
        setCases(caseList)
        setScenarios(scenarioList)
        setRealtime(statusValue)
        setAgentName(policy.persona.name)
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Falha ao carregar os dados da chamada'),
      )
  }, [variant])

  const teardown = useCallback(() => {
    realtimeHandle.current?.close()
    realtimeHandle.current = null
    analyserDispose.current?.()
    analyserDispose.current = null
    amplitude.current = null
  }, [])

  const hangup = useCallback(
    (notify = true) => {
      teardown()
      if (notify) publish({ type: 'ended', at: at() })
      setPhase('ended')
      setOrb('idle')
      setAgentPaused(false)
      setMuted(false)
    },
    [teardown, publish, at],
  )

  // ------------------------------------------------- comandos do supervisor --
  const onMessage = useCallback(
    (message: StreamMessage) => {
      switch (message.type) {
        case 'hello':
        case 'peers':
          setSupervisors(message.peers.supervisor ?? 0)
          break

        case 'command':
          if (message.command === 'mute') {
            setMuted(message.muted)
            realtimeHandle.current?.setMuted(message.muted)
          }
          if (message.command === 'takeover') {
            setAgentPaused(message.human)
            realtimeHandle.current?.setAgentPaused(message.human)
          }
          if (message.command === 'whisper') {
            realtimeHandle.current?.whisper(message.text)
            // Piscar aqui só para o demonstrador saber que chegou; numa ligação
            // de verdade a cliente não veria nada.
            setWhisperFlash(message.text)
            window.setTimeout(() => setWhisperFlash(null), 2600)
          }
          if (message.command === 'hangup' && phaseRef.current === 'live') hangup(false)
          break

        default:
          break
      }
    },
    [hangup],
  )

  useEffect(() => {
    stream.current = openStream('line', { onMessage })
    return () => {
      stream.current?.close()
      stream.current = null
      teardown()
    }
  }, [onMessage, teardown])

  // Relógio da chamada, espelhado para o console.
  useEffect(() => {
    if (phase !== 'live') return
    const id = window.setInterval(() => {
      setElapsed((current) => {
        const next = current + 1
        publish({ type: 'tick', elapsed: next })
        return next
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [phase, publish])

  // O QR do PIX é gerado aqui porque é esta tela que o exibe; o console só
  // espelha o SVG pronto. Compartilhado pelos dois modelos: no A depois de
  // executar a ferramenta, no B ao receber o artefato já executado.
  const publishArtifact = useCallback(
    async (artifact: Artifact, timeline: ToolExecutionResponse['timeline']) => {
      const enriched = { ...artifact }
      if (enriched.kind === 'pix' && enriched.code) {
        try {
          enriched.qr_svg = await QRCode.toString(enriched.code, {
            type: 'svg',
            margin: 0,
            errorCorrectionLevel: 'M',
          })
        } catch {
          enriched.qr_svg = null
        }
      }
      publish({ type: 'artifact', artifact: enriched, timeline })
      return enriched
    },
    [publish],
  )

  // ------------------------------------------------------------- iniciar --
  const start = useCallback(async () => {
    if (!debtor || !scenario) return
    setPhase('connecting')
    setError(null)
    setCaptions([])
    setElapsed(0)
    setHandoffReady(false)

    try {
      const call = await api.createCall(debtor.id, scenario.id, variant === 'b' ? 'pipeline' : 'rtc')
      callId.current = call.id

      // Anunciar antes de conectar: o console entra em modo espelho já com o
      // contexto certo, mesmo que a conexão demore.
      publish({
        type: 'dial',
        call_id: call.id,
        case_id: debtor.id,
        scenario_id: scenario.id,
        case_name: debtor.name,
        case_phone: debtor.phone,
        agent_name: agentName,
      })
      publish({ type: 'answered', at: '00:00' })

      // O contrato de callbacks é um só; os dois modelos alimentam a tela e o
      // console com os mesmos eventos.
      const callbacks = {
        onTranscriptDelta: (speaker: Speaker, delta: string) => {
          publish({ type: 'transcript', speaker, delta, at: at() })
          setCaptions((current) => {
            const last = current[current.length - 1]
            if (last && last.speaker === speaker) {
              const next = current.slice()
              next[next.length - 1] = { ...last, text: last.text + delta }
              return next.slice(-4)
            }
            captionSeq.current += 1
            return [...current, { id: captionSeq.current, speaker, text: delta }].slice(-4)
          })
        },
        onTurnDone: (speaker: Speaker) => publish({ type: 'turn_done', speaker }),
        onSpeakingChange: (state: OrbState) => {
          setOrb(state)
          publish({ type: 'orb', state })
        },
        onRemoteStream: (remote: MediaStream) => {
          analyserDispose.current?.()
          const analyser = createAmplitudeAnalyser(remote)
          amplitude.current = analyser.read
          analyserDispose.current = analyser.dispose
        },
        onAudit: (event: string) => {
          publish({ type: 'effect', effect: { type: 'audit', payload: { event } }, at: at() })
        },
        onError: (err: Error) => setError(err.message),
        onClose: () => {
          if (phaseRef.current === 'live') hangup()
        },
      }

      const handle =
        variant === 'b'
          ? await connectPipeline(
              {
                ...callbacks,
                onArtifact: (artifact, timeline, tool) => {
                  void publishArtifact(artifact, timeline)
                  if (tool === 'continuar_whatsapp') setHandoffReady(true)
                },
              },
              { callId: call.id, at },
            )
          : await connectRealtime(
              {
                ...callbacks,
                onToolCall: async (name, args) => {
                  const id = callId.current
                  if (!id) return { ok: false, error: 'chamada não registrada' }
                  try {
                    const response = await api.executeTool(id, name, args, at())
                    const artifact = await publishArtifact(response.artifact, response.timeline)
                    if (name === 'continuar_whatsapp') setHandoffReady(true)
                    return {
                      ok: true,
                      artifact_id: artifact.id,
                      title: artifact.title,
                      meta: artifact.meta,
                      visible_on_screen: true,
                    }
                  } catch (err) {
                    return {
                      ok: false,
                      error:
                        err instanceof Error
                          ? err.message
                          : `ferramenta ${name} não autorizada pela política`,
                    }
                  }
                },
              },
              { caseId: debtor.id },
            )

      realtimeHandle.current = handle
      setPhase('live')
      publish({ type: 'connected', model: handle.model, voice: handle.voice })
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'falha ao conectar'
      setError(reason)
      setPhase('ended')
      publish({ type: 'failed', reason })
    }
  }, [debtor, scenario, agentName, variant, publish, publishArtifact, at, hangup])

  const toggleMute = useCallback(() => {
    const next = !muted
    setMuted(next)
    realtimeHandle.current?.setMuted(next)
  }, [muted])

  const restart = useCallback(() => {
    setPhase('setup')
    setError(null)
    setCaptions([])
    setElapsed(0)
    setHandoffReady(false)
    callId.current = null
  }, [])

  const rtcReady = realtime?.enabled ?? false

  // ------------------------------------------------------------------ render --
  return (
    <div
      style={{
        minHeight: '100vh',
        background: color.panel,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: '100%',
          background: '#1b2330',
          borderRadius: 28,
          border: '1px solid rgba(255,255,255,.09)',
          boxShadow: shadow.modal,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 660,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            fontSize: 11,
            color: color.panelTextFaint,
            borderBottom: '1px solid rgba(255,255,255,.07)',
          }}
        >
          <span>{VARIANT_LABEL[variant]}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: supervisors > 0 ? color.green : color.inkGhost,
              }}
            />
            {supervisors > 0 ? `supervisor acompanhando (${supervisors})` : 'sem supervisor'}
          </span>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            padding: '22px 24px 30px',
            position: 'relative',
          }}
        >
          {phase === 'setup' ? (
            <Setup
              debtor={debtor}
              scenarios={scenarios}
              scenarioId={scenarioId}
              agentName={agentName}
              rtcReady={rtcReady}
              reason={realtime?.reason ?? null}
              error={error}
              onScenarioChange={setScenarioId}
              onStart={() => void start()}
            />
          ) : null}

          {(phase === 'connecting' || phase === 'live') && debtor ? (
            <>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{ fontSize: 11.5, color: color.panelTextFaint, letterSpacing: '.08em' }}
                >
                  BANCO VANTAGEM
                </div>
                <div style={{ fontSize: 23, fontWeight: 600, marginTop: 4 }}>{agentName}</div>
                <div style={{ fontSize: 13, color: color.panelTextMuted, marginTop: 6 }}>
                  {phase === 'connecting'
                    ? 'conectando…'
                    : agentPaused
                      ? 'atendente humano na linha'
                      : clock(elapsed)}
                </div>
              </div>

              <Orb state={agentPaused ? 'idle' : orb} amplitudeRef={amplitude} />

              {showCaptions && captions.length > 0 ? (
                // A fala mais NOVA entra por cima, inteira e maior; as
                // anteriores descem, encolhem e desvanecem. A altura fixa
                // corta as antigas embaixo — nunca a fala em curso, que era o
                // defeito da pilha cronológica.
                <div
                  aria-live="polite"
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    maxHeight: 190,
                    overflow: 'hidden',
                  }}
                >
                  {captions.slice(-3).reverse().map((caption, index) => (
                    <div
                      key={caption.id}
                      style={{
                        fontSize: index === 0 ? 14.5 : 12,
                        lineHeight: 1.5,
                        padding: index === 0 ? '10px 13px' : '7px 11px',
                        borderRadius: radius.xl,
                        background:
                          caption.speaker === 'ai'
                            ? 'rgba(240,134,47,.14)'
                            : 'rgba(255,255,255,.07)',
                        color: caption.speaker === 'ai' ? '#ffd9b8' : '#dfe5ef',
                        opacity: index === 0 ? 1 : index === 1 ? 0.5 : 0.28,
                        animation: 'capIn .3s ease',
                        transition: 'opacity .3s ease, font-size .3s ease, padding .3s ease',
                      }}
                    >
                      {caption.text}
                    </div>
                  ))}
                </div>
              ) : null}

              {whisperFlash ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 20,
                    right: 20,
                    fontSize: 11.5,
                    padding: '8px 12px',
                    borderRadius: radius.md,
                    background: 'rgba(139,92,246,.18)',
                    border: '1px solid rgba(139,92,246,.4)',
                    color: '#d6c6ff',
                    animation: 'mat .25s ease both',
                  }}
                >
                  supervisor sussurrou: “{whisperFlash}”
                </div>
              ) : null}

              {error ? (
                <div
                  style={{
                    fontSize: 12,
                    color: '#ffd9cf',
                    textAlign: 'center',
                    lineHeight: 1.5,
                    padding: '8px 12px',
                    borderRadius: radius.md,
                    background: 'rgba(237,79,46,.14)',
                  }}
                >
                  {error}
                </div>
              ) : null}

              {handoffReady ? (
                <button
                  type="button"
                  onClick={() => navigate('/whatsapp?continuar=1')}
                  style={{
                    background: '#00a884',
                    color: '#04231c',
                    border: 'none',
                    borderRadius: radius.pill,
                    padding: '13px 22px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    animation: 'mat .25s ease both',
                  }}
                >
                  Continuar no WhatsApp →
                </button>
              ) : null}

              <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
                <CircleButton
                  label={muted ? 'Reativar microfone' : 'Silenciar microfone'}
                  onClick={toggleMute}
                  active={muted}
                >
                  {muted ? '🔇' : '🎙'}
                </CircleButton>
                <CircleButton
                  label={showCaptions ? 'Ocultar legendas' : 'Mostrar legendas'}
                  onClick={() => setShowCaptions((v) => !v)}
                  active={!showCaptions}
                >
                  💬
                </CircleButton>
                <CircleButton label="Encerrar chamada" onClick={() => hangup()} danger>
                  ✕
                </CircleButton>
              </div>
            </>
          ) : null}

          {phase === 'ended' ? (
            <Ended
              error={error}
              onRestart={restart}
              onWhatsApp={handoffReady ? () => navigate('/whatsapp?continuar=1') : undefined}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Setup({
  debtor,
  scenarios,
  scenarioId,
  agentName,
  rtcReady,
  reason,
  error,
  onScenarioChange,
  onStart,
}: {
  debtor: Case | null
  scenarios: Scenario[]
  scenarioId: string
  agentName: string
  rtcReady: boolean
  reason: string | null
  error: string | null
  onScenarioChange(next: string): void
  onStart(): void
}) {
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: color.panelTextFaint, letterSpacing: '.08em' }}>
          SIMULAÇÃO DE CHAMADA
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 8, lineHeight: 1.35 }}>
          Você atende como
          <br />
          {debtor?.name ?? '—'}
        </div>
      </div>

      {debtor ? (
        <div
          style={{
            border: '1px solid rgba(255,255,255,.1)',
            borderRadius: radius.xxl,
            background: 'rgba(255,255,255,.04)',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: 12.5,
          }}
        >
          <Row label="Dívida">{brl(debtor.total_debt)}</Row>
          <Row label="Atraso">{debtor.days_overdue} dias</Row>
          <Row label="Segmento">
            {debtor.segment}
            {debtor.vip ? ' · VIP' : ''}
          </Row>
          <Row label="Quem liga">{agentName} · Banco Vantagem</Row>
        </div>
      ) : null}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span style={{ fontSize: 11.5, color: color.panelTextMuted, fontWeight: 600 }}>
          Situação a simular
        </span>
        <select
          value={scenarioId}
          onChange={(event) => onScenarioChange(event.target.value)}
          style={{
            padding: '11px 12px',
            borderRadius: radius.lg,
            border: `1px solid ${color.panelBorder}`,
            background: color.panelRaised,
            color: '#fff',
            fontSize: 13,
          }}
        >
          {scenarios.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11.5, color: color.panelTextDim, lineHeight: 1.5 }}>
          {scenarios.find((s) => s.id === scenarioId)?.description ?? ''}
        </span>
      </label>

      {!rtcReady ? (
        <div
          style={{
            fontSize: 12,
            color: '#ffd9cf',
            background: 'rgba(237,79,46,.12)',
            border: '1px solid rgba(237,79,46,.35)',
            borderRadius: radius.lg,
            padding: '10px 12px',
            lineHeight: 1.5,
          }}
        >
          {reason ?? 'Realtime API indisponível'} — defina a chave no <code>.env</code> e reinicie o
          backend.
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            fontSize: 12,
            color: '#ffd9cf',
            background: 'rgba(237,79,46,.12)',
            border: '1px solid rgba(237,79,46,.35)',
            borderRadius: radius.lg,
            padding: '10px 12px',
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onStart}
        disabled={!rtcReady || !debtor}
        style={{
          background: color.green,
          color: '#fff',
          border: 'none',
          borderRadius: radius.pill,
          padding: '16px 24px',
          fontSize: 15.5,
          fontWeight: 700,
          cursor: rtcReady && debtor ? 'pointer' : 'not-allowed',
          opacity: rtcReady && debtor ? 1 : 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        <span aria-hidden>✆</span> Atender a ligação
      </button>

      <div
        style={{
          fontSize: 11.5,
          color: color.panelTextDim,
          textAlign: 'center',
          lineHeight: 1.55,
        }}
      >
        O navegador vai pedir acesso ao microfone. Use fone de ouvido para a IA não
        escutar a própria voz.
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ color: color.panelTextFaint }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{children}</span>
    </div>
  )
}

function Ended({
  error,
  onRestart,
  onWhatsApp,
}: {
  error: string | null
  onRestart(): void
  onWhatsApp?(): void
}) {
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 17, fontWeight: 600 }}>
        {error ? 'Não foi possível conectar' : 'Chamada encerrada'}
      </div>
      {onWhatsApp ? (
        <button
          type="button"
          onClick={onWhatsApp}
          style={{
            background: '#00a884',
            color: '#04231c',
            border: 'none',
            borderRadius: radius.pill,
            padding: '13px 22px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Continuar no WhatsApp →
        </button>
      ) : null}
      {error ? (
        <div
          style={{
            fontSize: 12.5,
            color: '#ffd9cf',
            lineHeight: 1.55,
            maxWidth: 320,
            background: 'rgba(237,79,46,.12)',
            border: '1px solid rgba(237,79,46,.35)',
            borderRadius: radius.lg,
            padding: '10px 12px',
          }}
        >
          {error}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: color.panelTextMuted, lineHeight: 1.6, maxWidth: 300 }}>
          O resumo, os artefatos e a trilha de auditoria ficam no console de supervisão.
        </div>
      )}
      <button
        type="button"
        onClick={onRestart}
        style={{
          marginTop: 6,
          background: color.brand,
          border: 'none',
          color: '#fff',
          borderRadius: radius.pill,
          padding: '12px 22px',
          fontSize: 13.5,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Nova chamada
      </button>
    </div>
  )
}

function CircleButton({
  children,
  label,
  onClick,
  danger,
  active,
}: {
  children: React.ReactNode
  label: string
  onClick(): void
  danger?: boolean
  active?: boolean
}) {
  const background = danger
    ? '#c0392b'
    : active
      ? 'rgba(255,255,255,.22)'
      : 'rgba(255,255,255,.1)'

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        width: 54,
        height: 54,
        borderRadius: '50%',
        border: 'none',
        background,
        color: '#fff',
        fontSize: 20,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background .15s ease',
      }}
    >
      {children}
    </button>
  )
}
